import React from 'react';
import { Ticket } from '../types/ticketTypes';
import { useDragAndDrop } from './hooks/useDragAndDrop';
import { useWorkNotes } from './hooks/useWorkNotes';
import { useAppointments } from './hooks/useAppointments';
import { groupTicketsByAssignee, sortAssignees } from './utils/ticketUtils';
import { TicketRow } from './components/TicketRow';
import { TicketDetails } from './components/TicketDetails';
import { AssigneeGroupHeader } from './components/AssigneeGroupHeader';
import './themes/TicketTable-MinimalGray.css';

export interface TicketTableProps {
  tickets: Ticket[];
  selectedTicket: Ticket | null;
  onTicketSelect: (ticket: Ticket) => void;
  onTicketUpdate?: (ticket: Ticket) => void;
  onTicketReorder?: (tickets: Ticket[]) => void;
  onTicketReassign?: (ticketId: string, newAssignee: string) => void;
  currentUser?: string;
}

export const TicketTable: React.FC<TicketTableProps> = ({
  tickets,
  selectedTicket,
  onTicketSelect,
  onTicketUpdate,
  onTicketReorder,
  onTicketReassign,
  currentUser
}) => {
  // Initialize hooks
  const dragAndDropHandlers = useDragAndDrop({
    tickets,
    onTicketUpdate,
    onTicketReorder,
    onTicketReassign
  });

  const workNotesHandlers = useWorkNotes({
    onTicketUpdate,
    currentUser
  });

  const appointmentsHandlers = useAppointments({
    selectedTicket,
    onTicketUpdate,
    currentUser
  });

  // Group and sort tickets
  const groupedTickets = groupTicketsByAssignee(tickets);
  const sortedAssignees = sortAssignees(Object.keys(groupedTickets), currentUser);

  return (
    <div className="ticket-list">
      {sortedAssignees.map((assignee: string) => (
        <div key={assignee}>
          <AssigneeGroupHeader
            assignee={assignee}
            ticketCount={groupedTickets[assignee].length}
            currentUser={currentUser}
            isDragOverAssignee={dragAndDropHandlers.dragOverAssignee === assignee}
            dragHandlers={dragAndDropHandlers}
          />
          
          <div className="ticket-list-header">
            <span>Ticket ID</span>
            <span>Title</span>
            <span>Customer</span>
            <span>Status</span>
          </div>
          
          {groupedTickets[assignee].map((ticket: Ticket) => {
            const isDragging = dragAndDropHandlers.draggedTicket === ticket.id;
            const isDragOver = dragAndDropHandlers.dragOverTicket === ticket.id;
            const dragOverClass = isDragOver ? 
              (tickets.find(t => t.id === dragAndDropHandlers.draggedTicket)?.assignedTo === ticket.assignedTo ? 
                'drag-over' : 'drag-over-reassign') : '';
            
            return (
              <React.Fragment key={ticket.id}>
                <TicketRow
                  ticket={ticket}
                  isSelected={selectedTicket?.id === ticket.id}
                  isDragging={isDragging}
                  dragOverClass={dragOverClass}
                  onTicketSelect={onTicketSelect}
                  dragHandlers={dragAndDropHandlers}
                />
                
                {/* Expandable details section for selected ticket */}
                {selectedTicket?.id === ticket.id && (
                  <TicketDetails
                    ticket={ticket}
                    tickets={tickets}
                    workNotesHandlers={workNotesHandlers}
                    appointmentsHandlers={appointmentsHandlers}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      ))}
    </div>
  );
};